import React, { useEffect, useMemo, useState } from 'react';

import { TEXTS } from 'application/pages/texts';
import Button, { ButtonType } from '@zvuk-b2b/react-uikit/ui/Button';
import ModalScreen from '@zvuk-b2b/react-uikit/ui/ModalScreen';

import { useGetCompanyStreams } from '@entities/stream';

import InView from 'react-intersection-observer';
import AnalyticsService from 'application/services/AnalyticsService';
import { ThematicEventNameAMP } from 'application/services/AnalyticsService/ampTypes';
import styles from './index.module.scss';

import StreamPreviewCardSkeletons from './StreamPreviewCardSkeletons';
import StreamPreviewCardRenderer from './StreamPreviewCardRenderer';

export type CustomContentStreamSelectModalProps = {
  companyId?: string;
  onCancel: () => void;
  onSubmit: (selectedIds: string[], isAllSelected: boolean) => void;
  isRequestLoading: boolean;
  defaultSelectedStreams: string[] | undefined;
};

const CustomContentStreamSelectModal = (props: CustomContentStreamSelectModalProps) => {
  const { companyId, isRequestLoading, defaultSelectedStreams, onCancel, onSubmit } = props;

  const [isAllSelected, setIsAllSelected] = useState<boolean>(false);
  const [streamIds, setStreamIds] = useState<Map<string, boolean>>(
    new Map(defaultSelectedStreams?.map((id) => [id, true]))
  );

  const { companyStreams, withPoint, withoutPoint, isLoading, isFetching, hasNextPage, loadMore } =
    useGetCompanyStreams({
      companyId: companyId!,
    });

  const withPointIds = useMemo(() => withPoint.map((stream) => stream.id), [withPoint]);

  useEffect(() => {
    if (!defaultSelectedStreams && withPointIds) {
      setStreamIds((prevStreamIds) => {
        const newStreamIds = new Map(prevStreamIds);

        withPointIds.forEach((id) => {
          if (!newStreamIds.has(id)) {
            newStreamIds.set(id, true);
          }
        });

        return newStreamIds;
      });
    }
  }, [withPointIds, defaultSelectedStreams]);

  useEffect(() => {
    if (isAllSelected && companyStreams) {
      setStreamIds(new Map(companyStreams.map(({ id }) => [id, true])));
    }
  }, [isAllSelected, companyStreams]);

  const handleResetAll = () => {
    AnalyticsService.event(ThematicEventNameAMP.THEMATIC_REMOVE_ALL_CLICKED);
    setIsAllSelected(false);
    setStreamIds(new Map());
  };
  const handleSetAll = () => {
    AnalyticsService.event(ThematicEventNameAMP.THEMATIC_ADD_ALL_CLICKED);
    setIsAllSelected(true);
    setStreamIds(new Map(companyStreams?.map(({ id }) => [id, true])));
  };

  const handleSubmit = () => {
    const ids = Array.from(streamIds.keys());

    onSubmit(ids, isAllSelected);
  };

  return (
    <ModalScreen
      title={TEXTS.CUSTOM_CONTENT_MODAL_STREAMS_SELECT_TITLE}
      onClose={onCancel}
      containerClassName={styles.CustomModal}
      actionButtons={[
        <Button
          key="button_preview_custom_content_apply"
          elementId="button_preview_custom_content_apply"
          type={ButtonType.PRIMARY}
          onClick={handleSubmit}
          isLoading={isRequestLoading}>
          {TEXTS.CUSTOM_CONTENT_MODAL_STREAMS_SELECT_ACTION_BUTTON}
        </Button>,
      ]}>
      <div className={styles.CustomModalContainer}>
        <span className={styles.CustomModalContainerDescription}>
          {TEXTS.CUSTOM_CONTENT_MODAL_STREAMS_SELECT_DESCRIPTION}
        </span>

        <div className={styles.CustomModalContainerActions}>
          <Button
            elementId="stream_select_all_ny_button"
            type={ButtonType.PLAIN}
            onClick={handleSetAll}>
            {TEXTS.CUSTOM_CONTENT_MODAL_STREAMS_SELECT_ACTION_SELECT}
          </Button>
          {streamIds.size > 0 && (
            <>
              <span>•</span>
              <Button
                elementId="stream_deselect_all_ny_button"
                type={ButtonType.PLAIN}
                onClick={handleResetAll}>
                {TEXTS.CUSTOM_CONTENT_MODAL_STREAMS_SELECT_ACTION_DESELECT}
              </Button>
            </>
          )}
        </div>

        <div className={styles.CustomModalContainerStreams}>
          <StreamPreviewCardRenderer
            isLoading={isLoading}
            withPoint={withPoint}
            withoutPoint={withoutPoint}
            streamIds={streamIds}
            setStreamIds={setStreamIds}
            setIsAllSelected={setIsAllSelected}
          />

          {(hasNextPage || isFetching) && (
            <>
              <InView onChange={(inView) => inView && loadMore()} />
              <StreamPreviewCardSkeletons />
            </>
          )}
        </div>
      </div>
    </ModalScreen>
  );
};

export default CustomContentStreamSelectModal;
